import { describeError, useApi } from '@classgo/app-kit';
import { Button, Card, Modal, useToast } from '@classgo/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from '@tanstack/react-router';
import { useState } from 'react';

import { academicApi, gradingScaleQuery, listQuery } from '../../academic/api';
import type { PeriodView, SchoolYearView } from '../../academic/types';
import { ConfirmDialog } from '../../components/confirm';
import { EntityList } from '../../components/entity-list';
import { GradingScaleForm } from '../../forms/grading-scale-form';
import { PeriodForm } from '../../forms/period-form';
import { YearForm } from '../../forms/year-form';
import { useT } from '../../i18n';
import { daysBetween, formatDate } from '../../lib/dates';
import { StatusBadge } from '../years';

/**
 * The year as a bar with its periods laid on it: gaps and overlaps are visible at a glance.
 */
const PeriodTimeline = ({
  year,
  periods,
}: {
  year: SchoolYearView;
  periods: readonly PeriodView[];
}) => {
  const t = useT();
  const total = Math.max(1, daysBetween(year.startsOn, year.endsOn));
  const percent = (date: string) =>
    Math.min(100, Math.max(0, (daysBetween(year.startsOn, date) / total) * 100));
  return (
    <div className="w-full" aria-label={t('periods.timeline')} role="img">
      <div className="relative h-8 w-full overflow-hidden rounded-control bg-gray-100">
        {periods.map((period) => {
          const left = percent(period.startsOn);
          const width = Math.max(1, percent(period.endsOn) - left);
          return (
            <div
              key={period.id}
              title={`${period.name}: ${formatDate(period.startsOn)} → ${formatDate(period.endsOn)}`}
              className={`absolute top-0 h-full border-r border-white px-2 text-xs leading-8 ${
                period.status === 'active'
                  ? 'bg-brand-600 text-white'
                  : period.status === 'closed'
                    ? 'bg-gray-300 text-gray-700'
                    : 'bg-brand-100 text-brand-700'
              }`}
              style={{ left: `${left}%`, width: `${width}%` }}
            >
              <span className="block truncate">{period.name}</span>
            </div>
          );
        })}
      </div>
      <div className="mt-1 flex justify-between text-xs text-gray-500">
        <span>{formatDate(year.startsOn)}</span>
        <span>{formatDate(year.endsOn)}</span>
      </div>
    </div>
  );
};

/**
 * One school year: its dates and status, activation, the periods with their timeline and
 * open/close actions, and the grading scale editor.
 */
export const YearDetailScreen = () => {
  const t = useT();
  const api = useApi();
  const toast = useToast();
  const queryClient = useQueryClient();
  const { yearId = '' } = useParams({ strict: false });
  const years = useQuery(listQuery(api, 'years'));
  const periods = useQuery(listQuery(api, 'periods', { schoolYearId: yearId }));
  const scale = useQuery(gradingScaleQuery(api, yearId));
  const [editing, setEditing] = useState(false);
  const [activating, setActivating] = useState(false);
  const year = years.data?.items.find((candidate) => candidate.id === yearId) ?? null;

  const refresh = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: ['school', 'years'] }),
      queryClient.invalidateQueries({ queryKey: ['school', 'periods'] }),
    ]);
  const activate = useMutation({
    mutationFn: () => academicApi(api).activateYear(yearId),
    onSuccess: async () => {
      setActivating(false);
      toast.show({ tone: 'success', title: t('years.activated') });
      await refresh();
    },
    onError: (thrown) => toast.show({ tone: 'error', title: describeError(t, thrown) }),
  });
  const togglePeriod = useMutation({
    mutationFn: (period: PeriodView) =>
      period.status === 'active'
        ? academicApi(api).closePeriod(period.id)
        : academicApi(api).openPeriod(period.id),
    onSuccess: async (updated) => {
      toast.show({
        tone: 'success',
        title: updated.status === 'active' ? t('periods.opened') : t('periods.closed'),
      });
      await refresh();
    },
    onError: (thrown) => toast.show({ tone: 'error', title: describeError(t, thrown) }),
  });

  if (!year) {
    return (
      <p className="text-sm text-gray-500">
        {years.isPending ? t('common.loading') : t('api.error.NOT_FOUND')}
      </p>
    );
  }
  const sorted = [...(periods.data?.items ?? [])].sort((a, b) => a.position - b.position);
  const nextPosition = (sorted.at(-1)?.position ?? 0) + 1;

  return (
    <div className="space-y-4">
      <Card>
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h2 className="text-2xl font-semibold text-gray-900">{year.name}</h2>
            <p className="mt-1 text-sm text-gray-500">
              {t(`years.calendar.${year.calendar}`)} ·{' '}
              {t('years.detail.dates', {
                from: formatDate(year.startsOn),
                to: formatDate(year.endsOn),
              })}
            </p>
            <div className="mt-2">
              <StatusBadge status={year.status} />
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="secondary" onClick={() => setEditing(true)}>
              {t('years.edit')}
            </Button>
            {year.status !== 'active' ? (
              <Button onClick={() => setActivating(true)}>{t('years.activate')}</Button>
            ) : null}
          </div>
        </div>
      </Card>
      <EntityList
        entity="periods"
        query={{ schoolYearId: yearId }}
        title={t('periods.title')}
        caption={t('periods.caption')}
        createLabel={t('periods.new')}
        editTitle={t('periods.edit')}
        emptyTitle={t('periods.empty.title')}
        emptyBody={t('periods.empty.body')}
        toolbar={sorted.length > 0 ? <PeriodTimeline year={year} periods={sorted} /> : undefined}
        columns={[
          {
            key: 'position',
            header: t('periods.column.position'),
            cell: (row) => row.position,
            sortable: true,
            sortValue: (row) => row.position,
          },
          {
            key: 'name',
            header: t('periods.column.name'),
            cell: (row) => <span className="font-medium text-gray-900">{row.name}</span>,
          },
          {
            key: 'dates',
            header: t('periods.column.dates'),
            cell: (row) =>
              t('years.detail.dates', {
                from: formatDate(row.startsOn),
                to: formatDate(row.endsOn),
              }),
          },
          {
            key: 'status',
            header: t('periods.column.status'),
            cell: (row) => <StatusBadge status={row.status} />,
          },
          {
            key: 'actions',
            header: '',
            align: 'right',
            cell: (row) => (
              <Button
                size="sm"
                variant="ghost"
                onClick={(event) => {
                  event.stopPropagation();
                  togglePeriod.mutate(row);
                }}
              >
                {row.status === 'active' ? t('periods.close') : t('periods.open')}
              </Button>
            ),
          },
        ]}
        renderForm={({ current, onDone }) => (
          <PeriodForm
            schoolYearId={yearId}
            nextPosition={nextPosition}
            current={current}
            onDone={onDone}
          />
        )}
      />
      <Card title={t('scale.title')}>
        <p className="mb-4 text-sm text-gray-500">{t('scale.help')}</p>
        {scale.isPending ? (
          <p className="text-sm text-gray-500">{t('common.loading')}</p>
        ) : (
          <GradingScaleForm
            key={scale.data?.updatedAt ?? 'new'}
            yearId={yearId}
            current={scale.data ?? null}
          />
        )}
      </Card>
      <Modal open={editing} onClose={() => setEditing(false)} title={t('years.edit')}>
        {editing ? (
          <YearForm
            current={year}
            onDone={() => {
              setEditing(false);
              void refresh();
            }}
          />
        ) : null}
      </Modal>
      <ConfirmDialog
        open={activating}
        title={t('years.activate.title', { name: year.name })}
        body={t('years.activate.body')}
        confirmLabel={t('years.activate')}
        cancelLabel={t('common.cancel')}
        busy={activate.isPending}
        onConfirm={() => activate.mutate()}
        onClose={() => setActivating(false)}
      />
    </div>
  );
};
